import React, { createContext, useContext, useReducer, useMemo, useCallback, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const CreateRoundWizardContext = createContext(null)

export const useCreateRoundWizard = () => {
  const ctx = useContext(CreateRoundWizardContext)
  if (!ctx) {
    throw new Error('useCreateRoundWizard must be used within CreateRoundWizardProvider')
  }
  return ctx
}

const INITIAL_STATE = {
  mode: 'smart', // 'smart' | 'quick'

  // Step 1 - When
  dateRange: null, // smart: { start: ISO date, end: ISO date }
  date: null,      // quick: ISO date
  time: null,      // quick: 'HH:MM'

  // Step 2 - Details
  course: null,    // { name, latitude, longitude, ... } or { name }
  holeCount: 18,

  // Step 3 - Who
  who: null,                  // 'solo' | 'friends' | 'group'
  whoPickerOpen: false,       // true once friends/group card tapped and picker is shown
  selectedFriendIds: [],      // direct-invite friends
  anyFriends: true,           // 'Allow other friends to request to join' - open-invite broadcast
  slots: 4,                   // max_players when anyFriends is ON (includes creator)
  selectedGroupId: null,
  selectedGroupMemberIds: [],
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, mode: action.mode }
    case 'SET_WHEN':
      return { ...state, ...action.payload }
    case 'SET_DETAILS':
      return { ...state, course: action.course, holeCount: action.holeCount }
    case 'SET_WHO':
      return { ...state, who: action.who }
    case 'SET_WHO_PICKER_OPEN':
      return { ...state, whoPickerOpen: action.value }
    case 'SET_FRIEND_SELECTION':
      return { ...state, selectedFriendIds: action.ids }
    case 'TOGGLE_ANY_FRIENDS':
      return { ...state, anyFriends: action.value }
    case 'SET_SLOTS':
      return { ...state, slots: action.value }
    case 'SET_GROUP':
      return {
        ...state,
        selectedGroupId: action.groupId,
        selectedGroupMemberIds: action.memberIds ?? state.selectedGroupMemberIds,
      }
    case 'SET_GROUP_MEMBERS':
      return { ...state, selectedGroupMemberIds: action.memberIds }
    case 'HYDRATE':
      return { ...state, ...action.payload }
    case 'RESET':
      return { ...INITIAL_STATE, mode: action.mode ?? state.mode }
    default:
      return state
  }
}

export const CreateRoundWizardProvider = ({ children, initial }) => {
  const [state, dispatch] = useReducer(reducer, { ...INITIAL_STATE, ...(initial || {}) })
  const location = useLocation()

  // Reset wizard state when navigating away from /create-round/*
  useEffect(() => {
    if (!location.pathname.startsWith('/create-round/')) {
      dispatch({ type: 'RESET' })
    }
  }, [location.pathname])

  const setMode = useCallback((mode) => dispatch({ type: 'SET_MODE', mode }), [])
  const setWhen = useCallback((payload) => dispatch({ type: 'SET_WHEN', payload }), [])
  const setDetails = useCallback((course, holeCount) => dispatch({ type: 'SET_DETAILS', course, holeCount }), [])
  const setWho = useCallback((who) => dispatch({ type: 'SET_WHO', who }), [])
  const setWhoPickerOpen = useCallback((value) => dispatch({ type: 'SET_WHO_PICKER_OPEN', value }), [])
  const setFriendSelection = useCallback((ids) => dispatch({ type: 'SET_FRIEND_SELECTION', ids }), [])
  const toggleAnyFriends = useCallback((value) => dispatch({ type: 'TOGGLE_ANY_FRIENDS', value }), [])
  const setSlots = useCallback((value) => dispatch({ type: 'SET_SLOTS', value }), [])
  const setGroup = useCallback((groupId, memberIds) => dispatch({ type: 'SET_GROUP', groupId, memberIds }), [])
  const setGroupMembers = useCallback((memberIds) => dispatch({ type: 'SET_GROUP_MEMBERS', memberIds }), [])
  const hydrate = useCallback((payload) => dispatch({ type: 'HYDRATE', payload }), [])
  const reset = useCallback((mode) => dispatch({ type: 'RESET', mode }), [])

  const value = useMemo(
    () => ({
      state,
      setMode,
      setWhen,
      setDetails,
      setWho,
      setWhoPickerOpen,
      setFriendSelection,
      toggleAnyFriends,
      setSlots,
      setGroup,
      setGroupMembers,
      hydrate,
      reset,
    }),
    [state, setMode, setWhen, setDetails, setWho, setWhoPickerOpen, setFriendSelection, toggleAnyFriends, setSlots, setGroup, setGroupMembers, hydrate, reset],
  )

  return <CreateRoundWizardContext.Provider value={value}>{children}</CreateRoundWizardContext.Provider>
}

export default CreateRoundWizardContext
